import {Component, Input, OnInit} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../../destroyable.component';
import {
  EnrichedResponseDetail,
  EnrichedResponseDetailDescriptions,
  ResponseDetailSeverity
} from '../../sdconnect-ext.model';

@Component({
  selector: 'gp-sdc-person-feedback-enriched-response',
  templateUrl: './sdc-person-feedback-enriched-response.component.html',
  styleUrls: ['./sdc-person-feedback-enriched-response.component.scss']
})
export class SdcPersonFeedbackEnrichedResponseComponent extends Destroyable implements OnInit {

  public readonly ResponseDetailSeverity = ResponseDetailSeverity;

  private readonly DEFAULT_LOCALE = 'en';

  @Input()
  public enrichedResponse: EnrichedResponseDetail;

  public selectedLanguage = 'en';
  public languages: string[] = [];

  constructor(private translateService: TranslateService) {
    super();
  }

  ngOnInit() {
    this.initLanguages();
    this.selectLanguage();
    this.translateService.onLangChange
      .pipe(
        tap(() => this.selectLanguage()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  /**
   * Get a description for the selected language.
   * It handles also the old format where descriptions were stored with key/value (key = language).
   */
  public getDescription(): EnrichedResponseDetailDescriptions {
    if (Array.isArray(this.enrichedResponse?.descriptions)) {
      return this.enrichedResponse
        ?.descriptions
        ?.find(description => description.languageTag === this.selectedLanguage);
    } else {
      return this.enrichedResponse?.descriptions[this.selectedLanguage];
    }
  }

  /**
   * Select a language to display feedback with based on the current user language if possible.
   */
  private selectLanguage() {
    const currentLocale = this.translateService.currentLang;
    if (this.languages.includes(currentLocale)) {
      this.selectedLanguage = currentLocale;
      return;
    } else {
      const currentLanguage = currentLocale.split('-')[0];
      const matchingLanguages = this.languages.filter(language => language.startsWith(currentLanguage));
      if (matchingLanguages.length > 0) {
        this.selectedLanguage = matchingLanguages[0];
        return;
      }
    }
    this.selectedLanguage = this.languages.includes(this.DEFAULT_LOCALE) ? this.DEFAULT_LOCALE : this.languages[0];
  }

  /**
   * Initialize languages based on the enriched response descriptions.
   * It handles also the old format where descriptions were stored with key/value (key = language).
   */
  private initLanguages() {
    if (Array.isArray(this.enrichedResponse?.descriptions)) {
      this.languages = this.enrichedResponse?.descriptions?.map(description => description.languageTag);
    } else {
      // handle old format
      this.languages = Object.keys(this.enrichedResponse?.descriptions || {});
    }
  }
}
